import { useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Select, Space, Tag, Typography, Modal } from "antd";
import {
    BankOutlined, LinkOutlined, DisconnectOutlined, EnvironmentOutlined,
    PhoneOutlined, MailOutlined, ExclamationCircleFilled,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axiosInstance from "../../../../../variables/api/axiosInstance";

const { Title, Text } = Typography;
const { confirm } = Modal;
const PRIMARY = "#242166";

/**
 * Onglet « Conseil juridique » du dossier sinistre : associe / change / détache
 * un conseil juridique (personne morale) lié directement au dossier.
 * Endpoints : GET /api/conseil-juridique,
 * POST /api/sinistre/conseil-juridique/codeId/{sinistreId}/{conseilJuridiqueId},
 * DELETE /api/sinistre/conseil-juridique/codeId/{sinistreId}.
 */
const ConseilJuridiqueTab = ({ sinistre, setRefetch }) => {
    const readOnly = !!sinistre?.lectureSeule;
    const [conseils, setConseils] = useState([]);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);

    const current = sinistre?.conseilJuridique || null;

    useEffect(() => {
        axiosInstance.get("/api/conseil-juridique")
            .then((res) => setConseils(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des conseils juridiques."); });
    }, []);

    // Si aucun conseil n'est encore associé mais que la MISE EN CONTENTIEUX a précisé un cabinet,
    // on le PRÉ-SÉLECTIONNE dans le dropdown (l'utilisateur n'a plus qu'à cliquer « Associer »).
    useEffect(() => {
        if (current || !sinistre?.codeId) return;
        axiosInstance.get(`/api/sinistre/contentieux/${sinistre.codeId}`)
            .then((res) => {
                const cjId = res.data?.conseilJuridiqueId;
                if (cjId) setSelected((prev) => prev ?? cjId);
            })
            .catch(() => { /* pas de contentieux → rien à pré-sélectionner */ });
    }, [sinistre?.codeId, current]);

    const options = useMemo(() => conseils.map((c) => ({
        value: c.conseilJuridiqueId,
        label: c.nom,
        adresse: c.adresse,
        tel: c.telephone1,
    })), [conseils]);

    const refresh = () => setRefetch && setRefetch((p) => !p);

    const associer = () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        if (!selected || !sinistre?.codeId) return;
        setSaving(true);
        axiosInstance.post(`/api/sinistre/conseil-juridique/codeId/${sinistre.codeId}/${selected}`)
            .then(() => { toast.success("Conseil juridique associé au dossier."); setSelected(null); refresh(); })
            .catch((e) => { console.error(e); toast.error("Erreur lors de l'association."); })
            .finally(() => setSaving(false));
    };

    const detacher = () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        confirm({
            title: "Détacher le conseil juridique ?",
            icon: <ExclamationCircleFilled />,
            content: current?.nom,
            okText: "Détacher", okButtonProps: { danger: true }, cancelText: "Annuler",
            onOk: () => axiosInstance.delete(`/api/sinistre/conseil-juridique/codeId/${sinistre.codeId}`)
                .then(() => { toast.success("Conseil juridique détaché."); refresh(); })
                .catch((e) => { console.error(e); toast.error("Erreur lors du détachement."); }),
        });
    };

    return (
        <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                <BankOutlined style={{ fontSize: 22, color: PRIMARY }} />
                <Title level={5} style={{ margin: 0, color: PRIMARY }}>Conseil juridique du dossier</Title>
            </div>

            {/* Conseil actuellement associé */}
            {current ? (
                <div style={{
                    padding: 16, borderRadius: 12, background: "#f5f5ff",
                    border: "1px solid #e4e3ff", marginBottom: 22,
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Text strong style={{ fontSize: 16, color: PRIMARY }}>{current.nom}</Text>
                                <Tag color="success">Associé</Tag>
                            </div>
                            <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
                                {current.adresse && <Text type="secondary"><EnvironmentOutlined /> {current.adresse}</Text>}
                                {(current.telephone1 || current.telephone2) && (
                                    <Text type="secondary"><PhoneOutlined /> {[current.telephone1, current.telephone2].filter(Boolean).join(" / ")}</Text>
                                )}
                                {current.email && <Text type="secondary"><MailOutlined /> {current.email}</Text>}
                            </Space>
                        </div>
                        <Button danger icon={<DisconnectOutlined />} disabled={readOnly} onClick={detacher}>Détacher</Button>
                    </div>
                </div>
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun conseil juridique associé à ce dossier" style={{ marginBottom: 22 }} />
            )}

            {/* Associer / changer */}
            <Text type="secondary" style={{ fontSize: 13 }}>{current ? "Changer le conseil juridique" : "Associer un conseil juridique"}</Text>
            <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                <Select
                    showSearch allowClear style={{ flex: 1, minWidth: 260 }}
                    placeholder="Rechercher un conseil juridique…" optionFilterProp="label"
                    value={selected} onChange={setSelected} options={options}
                    optionRender={(opt) => (
                        <div>
                            <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
                            {(opt.data.adresse || opt.data.tel) && (
                                <div style={{ fontSize: 12, color: "#9ca3af" }}>{[opt.data.adresse, opt.data.tel].filter(Boolean).join(" · ")}</div>
                            )}
                        </div>
                    )}
                />
                <Button
                    type="primary" icon={<LinkOutlined />} loading={saving} disabled={!selected || readOnly}
                    onClick={associer} style={{ background: selected ? PRIMARY : undefined, border: "none" }}
                >
                    {current ? "Changer" : "Associer"}
                </Button>
            </div>
        </Card>
    );
};

export default ConseilJuridiqueTab;
